/* eslint-disable camelcase */
// image_url/image_path mirror the actual Postgres column names in
// supabase/schema.sql.
import { supabase, isSupabaseConfigured, PRODUCT_IMAGE_BUCKET } from "./supabaseClient";
import { processProductImage } from "./imageUtils";
import { Portfolios } from "../json/landingPageData";

export const PRODUCT_CATEGORIES = [
  "Sauces",
  "Mayonnaise",
  "Chutneys and Paste",
  "Vinegars and Flavours",
];

const LOCAL_KEY = "vrfood_products_v1";
const isBrowser = typeof window !== "undefined";

// ---------------------------------------------------------------------------
// Demo/offline adapter — used automatically whenever Supabase env vars are
// not set, so the admin dashboard is fully usable before any backend exists.
// Products (including images, stored as data URLs) live in localStorage.
// ---------------------------------------------------------------------------
function readLocal() {
  if (!isBrowser) return [];
  const raw = window.localStorage.getItem(LOCAL_KEY);
  if (!raw) {
    const seeded = Portfolios.map((item, index) => ({
      id: String(item.id ?? `seed_${index}`),
      title: item.title,
      type: item.type,
      packaging: item.packaging || [],
      image_url: item.imageUrl || "",
      created_at: new Date(Date.now() - (Portfolios.length - index) * 1000).toISOString(),
    }));
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(seeded));
    return seeded;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function writeLocal(products) {
  if (!isBrowser) return;
  window.localStorage.setItem(LOCAL_KEY, JSON.stringify(products));
}

function genId() {
  if (isBrowser && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const localAdapter = {
  async listProducts() {
    return [...readLocal()].sort((a, b) => {
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      return a.title.localeCompare(b.title);
    });
  },

  async addProduct({ title, type, packaging, imageFile }) {
    const products = readLocal();
    let image_url = "";
    if (imageFile) {
      const { dataUrl } = await processProductImage(imageFile);
      image_url = dataUrl;
    }
    const product = {
      id: genId(),
      title,
      type,
      packaging,
      image_url,
      created_at: new Date().toISOString(),
    };
    writeLocal([...products, product]);
    return product;
  },

  async updateProduct(id, { title, type, packaging, imageFile, removeImage }) {
    const products = readLocal();
    const existing = products.find((p) => p.id === id);
    if (!existing) throw new Error("Product not found.");

    let { image_url } = existing;
    if (imageFile) {
      const { dataUrl } = await processProductImage(imageFile);
      image_url = dataUrl;
    } else if (removeImage) {
      image_url = "";
    }

    const updated = { ...existing, title, type, packaging, image_url };
    writeLocal(products.map((p) => (p.id === id ? updated : p)));
    return updated;
  },

  async deleteProduct(id) {
    const products = readLocal();
    writeLocal(products.filter((p) => p.id !== id));
  },
};

// ---------------------------------------------------------------------------
// Supabase adapter — used automatically once REACT_APP_SUPABASE_URL /
// REACT_APP_SUPABASE_ANON_KEY are set at build time. Product images live in
// the "product-images" storage bucket; the DB row keeps both the public URL
// and the storage path so edits/deletes can clean up the old file.
// ---------------------------------------------------------------------------
async function uploadImage(imageFile) {
  const { blob } = await processProductImage(imageFile);
  const path = `products/${Date.now()}_${Math.random().toString(16).slice(2)}.jpg`;
  const { error: uploadError } = await supabase.storage
    .from(PRODUCT_IMAGE_BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", cacheControl: "31536000" });
  if (uploadError) throw uploadError;
  const { data } = supabase.storage.from(PRODUCT_IMAGE_BUCKET).getPublicUrl(path);
  return { image_url: data.publicUrl, image_path: path };
}

async function removeStoredImage(path) {
  if (!path) return;
  try {
    await supabase.storage.from(PRODUCT_IMAGE_BUCKET).remove([path]);
  } catch {
    // Best-effort cleanup — a stray file in storage is not worth failing the request over.
  }
}

const supabaseAdapter = {
  async listProducts() {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("type", { ascending: true })
      .order("title", { ascending: true });
    if (error) throw error;
    return data;
  },

  async addProduct({ title, type, packaging, imageFile }) {
    let image_url = "";
    let image_path = "";
    if (imageFile) {
      const uploaded = await uploadImage(imageFile);
      image_url = uploaded.image_url;
      image_path = uploaded.image_path;
    }
    const { data, error } = await supabase
      .from("products")
      .insert([{ title, type, packaging, image_url, image_path }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateProduct(id, { title, type, packaging, imageFile, removeImage }) {
    const { data: existing, error: fetchError } = await supabase
      .from("products")
      .select("image_path")
      .eq("id", id)
      .single();
    if (fetchError) throw fetchError;

    const patch = { title, type, packaging };

    if (imageFile) {
      const uploaded = await uploadImage(imageFile);
      patch.image_url = uploaded.image_url;
      patch.image_path = uploaded.image_path;
    } else if (removeImage) {
      patch.image_url = "";
      patch.image_path = "";
    }

    const { data, error } = await supabase
      .from("products")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;

    if ((imageFile || removeImage) && existing.image_path) {
      await removeStoredImage(existing.image_path);
    }

    return data;
  },

  async deleteProduct(id) {
    const { data: existing, error: fetchError } = await supabase
      .from("products")
      .select("image_path")
      .eq("id", id)
      .single();
    if (fetchError) throw fetchError;

    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) throw error;

    await removeStoredImage(existing.image_path);
  },
};

const adapter = isSupabaseConfigured ? supabaseAdapter : localAdapter;

export const usingDemoStorage = !isSupabaseConfigured;
export const { listProducts, addProduct, updateProduct, deleteProduct } = adapter;
